import React, { useEffect, useState } from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Stepper,
    Step,
    StepLabel,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import Logo from "../../assests/logo.png";
import { getCurrentUser } from "../../utils/auth";
import { markWelcomeScreenShown } from "../../api/api";
import TutorialModal from "../../components/TutorialModal";

const steps = [
    "Complete Your Profile",
    "Scan QR Code",
    "View Live Queue",
];

const Welcome = () => {
    const navigate = useNavigate();
    const currentUser = getCurrentUser();
    const [tutorialOpen, setTutorialOpen] = useState(false);

    useEffect(() => {
        // Mark welcome screen as shown
        if (currentUser?.email) {
            markWelcomeScreenShown(currentUser.email).catch(err => {
                console.error("Error marking welcome screen as shown:", err);
            });
            localStorage.setItem('welcome_screen_shown', 'true');
        }

        // Show tutorial modal for new free trial users
        // Check if tutorial was already shown
        const tutorialShown = localStorage.getItem('tutorial_shown');
        if (!tutorialShown) {
            // Show tutorial after a short delay
            const timer = setTimeout(() => {
                setTutorialOpen(true);
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [currentUser]);

    const handleGetStarted = () => {
        navigate("/home/profile");
    };

    const handleTutorialClose = () => {
        setTutorialOpen(false);
    };

    const handleTutorialComplete = () => {
        localStorage.setItem('tutorial_shown', 'true');
    };

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "100vh",
                px: { xs: 2, sm: 3 },
                py: { xs: 3, sm: 4 },
                backgroundColor: "#f5f5f5",
            }}
        >
            <Card
                sx={{
                    maxWidth: 600,
                    width: "100%",
                    boxShadow: 3,
                    borderRadius: 2,
                }}
            >
                <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            mb: 4,
                        }}
                    >
                        <img
                            src={Logo}
                            alt="Logo"
                            style={{
                                width: 150,
                                maxWidth: "100%",
                                height: "auto",
                                marginBottom: 24,
                            }}
                        />
                        <Typography
                            variant="h4"
                            component="h1"
                            gutterBottom
                            sx={{
                                fontWeight: 600,
                                textAlign: "center",
                                fontSize: { xs: "1.75rem", sm: "2rem" },
                            }}
                        >
                            Welcome to easyQue!
                        </Typography>
                        <Typography
                            variant="body1"
                            color="text.secondary"
                            sx={{
                                textAlign: "center",
                                mt: 2,
                                mb: 4,
                            }}
                        >
                            Let's get you started with your free trial. Follow these simple steps to set up your queue system.
                        </Typography>
                    </Box>

                    <Box sx={{ mb: 4 }}>
                        <Stepper activeStep={0} orientation="vertical">
                            {steps.map((label, index) => (
                                <Step key={label}>
                                    <StepLabel
                                        StepIconComponent={() => (
                                            <Box
                                                sx={{
                                                    width: 40,
                                                    height: 40,
                                                    borderRadius: "50%",
                                                    backgroundColor: "primary.main",
                                                    color: "white",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    fontWeight: "bold",
                                                }}
                                            >
                                                {index + 1}
                                            </Box>
                                        )}
                                    >
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            {label}
                                        </Typography>
                                    </StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                    </Box>

                    <Box sx={{ display: "flex", gap: 2, mt: 4 }}>
                        <Button
                            variant="contained"
                            color="primary"
                            fullWidth
                            size="large"
                            onClick={handleGetStarted}
                            sx={{
                                py: 1.5,
                                fontSize: "1rem",
                                textTransform: "none",
                                fontWeight: 600,
                            }}
                        >
                            Get Started
                        </Button>
                    </Box>
                </CardContent>
            </Card>

            {/* Tutorial Modal for new free trial users */}
            <TutorialModal
                open={tutorialOpen}
                onClose={handleTutorialClose}
                onComplete={handleTutorialComplete}
            />
        </Box>
    );
};

export default Welcome;
